/**
 * Cliente do BFF/svc-decision — e a lista completa do que o navegador sabe pedir.
 *
 * As chamadas de painel não aceitam medida, dimensão ou filtro livre, e isso
 * não é economia de código: é o ADR-05 chegando à borda. Se amanhã alguém
 * precisar de um recorte novo, o caminho é nomear um bloco em
 * `services/svc-analytics-api/src/catalogo.js` — onde há teste, revisão e um
 * nome que o Copilot também pode usar — e não montar uma consulta aqui.
 *
 * As chamadas de decisão (recomendações) seguem a mesma regra por outro
 * ângulo: a tela não decide qual botão mostrar nem por que uma ação é
 * recusada — o `acoes_disponiveis` e os erros de transição vêm prontos do
 * `svc-decision`, que lê a mesma máquina de estados do motor.
 */

export type Painel = {
  painel: string;
  titulo: string;
  periodo: { nome: string; rotulo: string; de: string; ate: string };
  filial: string | null;
  tenant_id: string;
  blocos: Record<string, Array<Record<string, string | number>>>;
  _meta: { modo: "demo" | "semantica"; ms: number };
};

export type Catalogo = {
  paineis: Array<{ nome: string; titulo: string; descricao: string; blocos: string[] }>;
  periodos: Array<{ nome: string; rotulo: string }>;
};

/** Erro com o status HTTP preservado — a tela trata 403 diferente de 502. */
export class ErroDeApi extends Error {
  status: number;
  constructor(mensagem: string, status: number) {
    super(mensagem);
    this.status = status;
  }
}

/**
 * O token vem do login (svc-identity) e fica em memória, não em `localStorage`.
 *
 * Token em `localStorage` é legível por qualquer script que entre na página, e
 * sobrevive ao fechar a aba. Em memória, um XSS precisa acontecer **durante** a
 * sessão para valer alguma coisa, e o custo é ter que autenticar de novo ao
 * recarregar — que é o comportamento certo para um painel com dado de cliente.
 */
let token: string | null = null;
export const guardarToken = (t: string | null) => { token = t; };
export const obterToken = () => token;

/**
 * A tela chama isto uma vez, no topo, pra saber quando deslogar sozinha — sem
 * isso, cada chamada nova (painel, decisão, explicação) teria que repetir a
 * mesma checagem de 401. Um único ponto aqui é o que garante que "sessão
 * expirou" tem um comportamento e não um por tela.
 */
let aoDeslogar: (() => void) | null = null;
export const definirAoDeslogar = (fn: (() => void) | null) => { aoDeslogar = fn; };

async function pegar<T>(caminho: string): Promise<T> {
  const r = await fetch(caminho, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  return respostaOuErro<T>(r);
}

async function enviar<T>(caminho: string, corpo: unknown): Promise<T> {
  const r = await fetch(caminho, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(corpo),
  });
  return respostaOuErro<T>(r);
}

async function respostaOuErro<T>(r: Response): Promise<T> {
  if (!r.ok) {
    const corpo = await r.json().catch(() => ({}));
    if (r.status === 401) { guardarToken(null); aoDeslogar?.(); }
    throw new ErroDeApi((corpo as { error?: string }).error ?? `HTTP ${r.status}`, r.status);
  }
  return (await r.json()) as T;
}

/** Único endpoint público — nunca leva o Authorization de uma sessão anterior. */
export async function login(email: string, password: string): Promise<void> {
  const r = await fetch("/api/auth/login", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const { token: t } = await respostaOuErro<{ token: string }>(r);
  guardarToken(t);
}

export const carregarCatalogo = () => pegar<Catalogo>("/api/analytics/catalogo");

/**
 * Pede um painel pelo nome. Período e filial são os únicos parâmetros — e o BFF
 * recusa qualquer outro em vez de ignorar, então um `?measures=` colado na URL
 * volta 400 e não um painel plausível.
 */
export function carregarPainel(
  painel: string,
  opcoes: { periodo?: string; filial?: string | null } = {},
): Promise<Painel> {
  const q = new URLSearchParams();
  if (opcoes.periodo) q.set("periodo", opcoes.periodo);
  if (opcoes.filial) q.set("filial", opcoes.filial);
  const busca = q.toString();
  return pegar<Painel>(`/api/analytics/paineis/${encodeURIComponent(painel)}${busca ? `?${busca}` : ""}`);
}

// -- caixa de decisão (svc-decision) ----------------------------------------- //

export type Recomendacao = {
  id: string;
  tenant_id: string;
  tipo: string;
  modulo: string;
  produto: string;
  filial: string;
  problema: string;
  causa: string;
  acao: Record<string, unknown>;
  impacto_estimado: { economia_brl: number; metrica: string };
  confianca: number;
  urgencia: number;
  score: number;
  alcada: string | null;
  explicacao_id: string;
  narrativa: string;
  estado: string;
  expira_em: string;
  gerada_em: string;
  acoes_disponiveis: string[];
};

export type MotivoRejeicao = {
  codigo: string;
  rotulo: string;
  suprime_dias: number;
  exige_texto?: boolean;
};

export type Fila = {
  recomendacoes: Recomendacao[];
  motivos_rejeicao: MotivoRejeicao[];
};

export type Fator = { chave: string; rotulo: string; valor: number; unidade: string; origem: string };
export type Passo = { destino: string; operacao: string; entradas: unknown[]; rotulo: string; resultado: number };
export type Evidencia = { descricao: string } & Record<string, unknown>;
export type RegraAplicada = { regra: string; efeito: string };

export type Explicacao = {
  explicacao: {
    id: string;
    titulo: string;
    formula: string;
    unidade: string;
    fatores: Fator[];
    passos: Passo[];
    evidencias: Evidencia[];
    regras_aplicadas: RegraAplicada[];
    resultado: number;
  };
  narrativa: string;
  componentes_confianca: Record<string, number>;
};

export const carregarRecomendacoes = (estado?: string) =>
  pegar<Fila>(`/v1/recommendations${estado ? `?estado=${encodeURIComponent(estado)}` : ""}`);

export const carregarExplicacao = (id: string) =>
  pegar<Explicacao>(`/v1/explain/${encodeURIComponent(id)}`);

/** `motivo`/`texto` só importam quando `acao` é "rejeitar" — o backend recusa quem tentar sem. */
export function enviarFeedback(
  id: string,
  acao: string,
  opcoes: { motivo?: string; texto?: string } = {},
) {
  return enviar<{ estado: string }>(`/v1/recommendations/${encodeURIComponent(id)}/feedback`, {
    acao, ...opcoes,
  });
}
